<template>
  <div class="file-edit-container">
    <el-form 
      :model="form" 
      :rules="rules" 
      ref="editForm" 
      label-width="100px"
      class="edit-form"
    >
      <!-- 第一行：文件名称 + 文件类型 -->
      <div class="form-row">
        <el-form-item label="文件名称" prop="name" class="form-item-half">
          <el-input 
            v-model="form.name" 
            placeholder="请输入文件名称"
            clearable
            maxlength="100"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="文件类型" prop="type" class="form-item-half">
          <el-select 
            v-model="form.type" 
            placeholder="请选择文件类型"
            style="width: 100%"
          >
            <el-option label="PPT" :value="1" />
            <el-option label="Word" :value="2" />
          </el-select>
        </el-form-item>
      </div>

      <!-- 第二行：作者 + 页数 -->
      <div class="form-row">
        <el-form-item label="作者" prop="author" class="form-item-half">
          <el-input 
            v-model="form.author" 
            placeholder="请输入作者名称"
            clearable
            maxlength="50"
          />
        </el-form-item>
        <el-form-item label="页数" prop="page" class="form-item-half">
          <el-input-number 
            v-model="form.page" 
            :min="1" 
            :max="9999"
            placeholder="页数"
            style="width: 100%"
          />
        </el-form-item>
      </div>

      <!-- 第三行：价格 + 状态 -->
      <div class="form-row">
        <el-form-item label="价格" prop="price" class="form-item-half">
          <el-input-number 
            v-model="form.price" 
            :min="0" 
            :max="9999.99"
            :precision="2"
            placeholder="价格"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="状态" prop="state" class="form-item-half">
          <el-select 
            v-model="form.state" 
            placeholder="请选择状态"
            style="width: 100%"
          >
            <el-option label="正常" :value="1" />
            <el-option label="下架" :value="0" />
          </el-select>
        </el-form-item>
      </div>

      <!-- 第四行：文件格式 + 分类（仅新文科文件） -->
      <div class="form-row" v-if="isXinWenKeFile(fileData)">
        <el-form-item label="文件格式" prop="format" class="form-item-half">
          <el-select 
            v-model="form.format" 
            placeholder="请选择文件格式"
            style="width: 100%"
          >
            <el-option
              v-for="format in fileFormats"
              :key="format.format"
              :label="format.name"
              :value="format.format"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="大分类" prop="categoryId" class="form-item-half">
          <el-select 
            v-model="form.categoryId" 
            placeholder="请选择大分类"
            style="width: 100%"
            @change="onCategoryChange"
          >
            <el-option
              v-for="category in categories"
              :key="category.id"
              :label="category.name"
              :value="category.id"
            />
          </el-select>
        </el-form-item>
      </div>

      <!-- 第四行：文件格式（仅旧文件） -->
      <div class="form-row" v-if="!isXinWenKeFile(fileData)">
        <el-form-item label="文件格式" prop="format" class="form-item-half">
          <el-select 
            v-model="form.format" 
            placeholder="请选择文件格式"
            style="width: 100%"
          >
            <el-option
              v-for="format in fileFormats"
              :key="format.format"
              :label="format.name"
              :value="format.format"
            />
          </el-select>
        </el-form-item>
        <el-form-item class="form-item-half">
          <!-- 占位 -->
        </el-form-item>
      </div>

      <!-- 第五行：子分类（仅新文科文件） -->
      <div class="form-row" v-if="isXinWenKeFile(fileData)">
        <el-form-item label="子分类" prop="subcategoryId" class="form-item-half">
          <el-select 
            v-model="form.subcategoryId" 
            :placeholder="form.categoryId ? '请选择子分类' : '请先选择大分类'"
            style="width: 100%"
            :disabled="!form.categoryId"
          >
            <el-option
              v-for="subcategory in subcategories"
              :key="subcategory.id"
              :label="subcategory.name"
              :value="subcategory.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item class="form-item-half">
          <!-- 占位 -->
        </el-form-item>
      </div>

      <!-- 第六行：描述 -->
      <div class="form-row">
        <el-form-item label="描述" prop="description" class="form-item-full">
          <el-input 
            v-model="form.description" 
            type="textarea" 
            :rows="3"
            placeholder="请输入文件描述"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
      </div>

      <!-- 文件信息显示区域 -->
      <div class="file-info-section">
        <h4>当前文件信息</h4>
        <div class="file-info-grid">
          <div class="file-info-item">
            <span class="label">文件：</span>
            <span class="value">{{ originalFile.name || '无' }}</span>
          </div>
          <div class="file-info-item">
            <span class="label">封面：</span>
            <span class="value">{{ originalFile.cover ? '已设置' : '无' }}</span>
          </div>
          <div class="file-info-item">
            <span class="label">预览图：</span>
            <span class="value">{{ originalFile.images ? originalFile.images.split(',').length + '张' : '无' }}</span>
          </div>
        </div>
      </div>

      <!-- 封面和预览图区域 -->
      <div class="file-media-section">
        <h4>封面和预览图</h4>
        <p class="media-tip">如需更新封面或预览图，请上传新图片。不选择则保持原图不变。</p>

        <!-- 封面图片上传 -->
        <el-form-item label="封面图片" class="file-upload-item">
          <el-upload
            :key="`cover-upload-${fileData?.id || 'new'}`"
            ref="coverUpload"
            :action="''"
            :file-list="coverFileList"
            :before-upload="beforeCoverUpload"
            :on-change="onCoverChange"
            :on-remove="onCoverRemove"
            :accept="acceptedImageTypes"
            :limit="1"
            :auto-upload="false"
            list-type="picture-card"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
          <div class="upload-tip">支持JPG、PNG格式，建议尺寸16:9</div>
        </el-form-item>

        <!-- 预览图片上传 -->
        <el-form-item label="预览图片" class="file-upload-item">
          <el-upload
            :key="`image-upload-${fileData?.id || 'new'}`"
            ref="imageUpload"
            :action="''"
            :file-list="imageFileList"
            :before-upload="beforeImageUpload"
            :on-change="onImageChange"
            :on-remove="onImageRemove"
            :accept="acceptedImageTypes"
            :limit="3"
            :multiple="true"
            :auto-upload="false"
            list-type="picture-card"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
          <div class="upload-tip">最多3张，支持JPG、PNG格式，可多选</div>
        </el-form-item>
      </div>

      <!-- 操作按钮 -->
      <div class="form-actions">
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">
          {{ submitting ? '保存中...' : '保存修改' }}
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import api from '@/api/api';

export default {
  name: 'FileEdit',
  props: {
    // 当前模块类型
    module: {
      type: String,
      default: 'all'
    },
    // 要编辑的文件数据
    fileData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      submitting: false,
      currentFileId: null, // 当前正在编辑的文件ID，用于避免重复初始化
      form: {
        name: '',
        type: 1,
        author: '',
        page: 1,
        price: 0,
        state: 1,
        format: '',
        categoryId: null,
        subcategoryId: null,
        description: '',
        remark: '',
        fileUrl: '',
        cover: '',
        images: []
      },
      originalFile: {},
      rules: {
        name: [
          { required: true, message: '请输入文件名称', trigger: 'blur' },
          { min: 2, max: 100, message: '文件名称长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        type: [
          { required: true, message: '请选择文件类型', trigger: 'change' }
        ],
        author: [
          { required: true, message: '请输入作者名称', trigger: 'blur' },
          { min: 2, max: 50, message: '作者名称长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        page: [
          { required: true, message: '请输入页数', trigger: 'blur' },
          { type: 'number', min: 1, max: 9999, message: '页数必须在 1 到 9999 之间', trigger: 'blur' }
        ],
        price: [
          { required: true, message: '请输入价格', trigger: 'blur' },
          { type: 'number', min: 0, max: 9999.99, message: '价格必须在 0 到 9999.99 之间', trigger: 'blur' }
        ],
        state: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ],
         format: [
           { required: true, message: '请选择文件格式', trigger: 'change' }
         ],
         categoryId: [
           { required: true, message: '请选择大分类', trigger: 'change' }
         ],
         subcategoryId: [
           { required: true, message: '请选择子分类', trigger: 'change' }
         ]
      },
      coverFileList: [],
      imageFileList: [],
      newCoverFile: null, // 新选择的封面文件
      newImageFiles: [], // 新选择的预览图片文件
      incrementalUpdate: true, // 使用增量更新模式
      deleteCoverUrl: '', // 要删除的封面图URL
      deletePreviewUrls: [], // 要删除的预览图URL列表
      categories: [],
      subcategories: [],
      allSubcategories: [],
      fileFormats: [],
      acceptedImageTypes: '.jpg,.jpeg,.png'
    };
  },
  computed: {
    // 根据文件编码判断是否显示分类字段
  },
  watch: {
    fileData: {
      handler(newVal) {
        if (newVal && newVal.id !== this.currentFileId) {
          this.currentFileId = newVal.id;
          this.initFormData();
          // 强制重新渲染上传组件
          this.$nextTick(() => {
            this.forceUpdateUploadComponents();
          });
        }
      },
      deep: true
    }
  },
   async mounted() {
     await Promise.all([
       this.loadCategories(),
       this.loadFileFormats(),
       this.loadSubcategories()
     ]);
     
     // 数据加载完成后，如果有文件数据，重新初始化表单
     if (this.fileData) {
       this.initFormData();
     }
   },

   beforeDestroy() {
     // 清理创建的Object URLs以防止内存泄漏
     // 清理预览图片的Object URLs
     this.imageFileList.forEach(file => {
       if (file.raw && file.url && file.url.startsWith('blob:')) {
         URL.revokeObjectURL(file.url);
       }
     });
     // 清理封面图片的Object URL
     this.coverFileList.forEach(file => {
       if (file.raw && file.url && file.url.startsWith('blob:')) {
         URL.revokeObjectURL(file.url);
       }
     });
   },
  methods: {
    // 判断是否为新文科文件（根据文件编码）
    isXinWenKeFile(fileData) {
      if (!fileData || !fileData.code) return false;
      // 新文科文件编码普遍以UP开头
      return fileData.code.startsWith('UP');
    },

     // 初始化表单数据
     initFormData() {
       if (this.fileData) {
         this.originalFile = { ...this.fileData };
         // 处理预览图数据
         let previewImages = [];
         if (this.fileData.images) {
           previewImages = this.fileData.images.split(',').filter(url => url.trim());
         } else if (this.fileData.image) {
           previewImages = this.fileData.image.split(',').filter(url => url.trim());
         }

         this.form = {
           name: this.fileData.name || '',
           type: this.fileData.type || 1,
           author: this.fileData.author || '',
           page: this.fileData.page || 1,
           price: this.fileData.price || 0,
           state: this.fileData.state !== undefined ? this.fileData.state : 1,
           format: this.fileData.format || '',
           categoryId: this.fileData.categoryId || null,
           subcategoryId: this.fileData.subcategoryId || null,
           description: this.fileData.description || this.fileData.describes || '',
           remark: this.fileData.remark || '',
           fileUrl: this.fileData.fileUrl || this.fileData.url || '',
           cover: this.fileData.cover || '',
           images: previewImages
         };
         
         // 如果有分类ID，需要加载对应的子分类
         if (this.form.categoryId && this.allSubcategories.length > 0) {
           this.subcategories = this.allSubcategories.filter(sub => sub.categoryId === this.form.categoryId);
         }
         
         // 初始化文件列表显示
         this.initFileLists();
       }
     },

    // 初始化媒体文件列表
    initFileLists() {
      // 清空文件列表和新的文件选择
      this.coverFileList = [];
      this.imageFileList = [];
      this.newCoverFile = null;
      this.newImageFiles = [];
      this.deleteCoverUrl = '';
      this.deletePreviewUrls = [];

      // 封面图片处理
      console.log('初始化封面图列表，form.cover:', this.form.cover);
      console.log('fileData.cover:', this.fileData?.cover);
      console.log('newCoverFile:', this.newCoverFile);

      // 优先显示新选择的封面图片
      if (this.newCoverFile) {
        console.log('显示新选择的封面图:', this.newCoverFile.name);
        this.coverFileList = [{
          name: this.newCoverFile.name,
          url: URL.createObjectURL(this.newCoverFile),
          raw: this.newCoverFile,
          status: 'success'
        }];
      } else if (this.form.cover) {
        console.log('设置封面图到fileList:', this.form.cover);
        this.coverFileList = [{
          name: '封面图片',
          url: this.form.cover,
          status: 'success'
        }];
        console.log('coverFileList设置完成:', this.coverFileList);
      } else {
        console.log('无封面图，coverFileList保持为空');
        // 确保coverFileList是空数组，这样el-upload不会显示已上传的文件
        this.coverFileList = [];
      }

      // 预览图片处理 - 直接使用form.images（已在initFormData中统一处理为数组格式）
      const existingImages = [];
      if (this.form.images && this.form.images.length > 0) {
        existingImages.push(...this.form.images.map((url, index) => ({
          name: `预览图${index + 1}`,
          url: url.trim(),
          status: 'success'
        })));
      }

      // 添加新选择的图片（如果有的话）
      const newImages = this.newImageFiles.map((file, index) => ({
        name: file.name,
        url: URL.createObjectURL(file), // 生成预览URL
        raw: file, // 保留原始文件对象用于上传
        status: 'success'
      }));

      // 合并现有图片和新图片
      this.imageFileList = [...existingImages, ...newImages];

      // 文件数据初始化完成
    },

     // 加载分类数据
     async loadCategories() {
       try {
         const response = await api.getCategories();
         if (response.data && response.data.code === 200) {
           this.categories = response.data.data || [];
           // 分类数据加载成功
         }
       } catch (error) {
         // 分类数据加载失败，静默处理
       }
     },

    // 加载文件格式数据
    async loadFileFormats() {
      try {
        const response = await api.getFileFormats();
        if (response.data && response.data.code === 200) {
          this.fileFormats = response.data.data || [];
        }
      } catch (error) {
        // 静默处理错误
      }
    },

     // 加载子分类数据
     async loadSubcategories() {
       try {
         const response = await api.getSubcategories();
         if (response.data && response.data.code === 200) {
           this.allSubcategories = response.data.data || [];
           // 子分类数据加载成功
         }
       } catch (error) {
         // 子分类数据加载失败，静默处理
       }
     },

     // 分类变化处理
     onCategoryChange(categoryId) {
       // 大分类变化处理
       this.form.subcategoryId = null;
       this.subcategories = this.allSubcategories.filter(sub => sub.categoryId === categoryId);
       // 过滤后的子分类已更新
       
       // 如果选择了大分类，清空子分类选择并触发验证
       if (categoryId) {
         this.$nextTick(() => {
           this.$refs.editForm.validateField('subcategoryId');
         });
       }
     },


    // 封面图片上传前处理
    beforeCoverUpload(file) {
      const isValidType = this.acceptedImageTypes.split(',').some(type =>
        file.name.toLowerCase().endsWith(type.replace('.', ''))
      );
      if (!isValidType) {
        this.$message.error('只能上传JPG、PNG图片！');
        return false;
      }
      const isValidSize = file.size / 1024 / 1024 < 5;
      if (!isValidSize) {
        this.$message.error('图片大小不能超过5MB！');
        return false;
      }
      // 存储新选择的文件
      this.newCoverFile = file;
      return true;
    },

    // 封面图片选择变化处理
    onCoverChange(file, fileList) {
      // 当文件被移除时，清空新选择的文件
      if (fileList.length === 0 || file.status === 'removed') {
        this.newCoverFile = null;
        this.coverFileList = [];
      } else if (file.raw && file.status !== 'removed') {
        // 当有新文件被添加时，设置新选择的文件并更新显示列表
        this.newCoverFile = file.raw;
        // 更新显示列表 - 显示新选择的封面图片
        this.coverFileList = [{
          name: file.name,
          url: URL.createObjectURL(file.raw), // 生成预览URL
          raw: file.raw, // 保留原始文件对象用于上传
          status: 'success'
        }];
      } else if (fileList.length > 0) {
        // 如果fileList有文件但没有raw属性，尝试从fileList中找到新文件
        const newFile = fileList.find(f => f.raw && f.status !== 'removed');
        if (newFile) {
          this.newCoverFile = newFile.raw;
          this.coverFileList = [{
            name: newFile.name,
            url: URL.createObjectURL(newFile.raw),
            raw: newFile.raw,
            status: 'success'
          }];
        }
      }
    },

    // 封面图片移除
    onCoverRemove() {
      // 如果原来有封面，则记录要删除的封面URL
      if (this.form.cover) {
        this.deleteCoverUrl = this.form.cover;
      }
      this.form.cover = '';
      this.newCoverFile = null;
      // 清空显示列表
      this.coverFileList = [];
    },

    // 预览图片上传前处理
    beforeImageUpload(file) {
      return true;
    },

    // 预览图片选择变化处理
    onImageChange(file, fileList) {
      // 更新新选择的文件列表 - 保留所有新上传的文件（有 raw 属性）
      // 过滤掉已被移除的文件
      this.newImageFiles = fileList
        .filter(f => f.raw && f.status !== 'removed')
        .map(f => f.raw);

      // 更新显示列表 - 合并现有图片和新选择的图片
      const existingImages = this.imageFileList.filter(f => f.url && !f.raw); // 现有图片
      const newImages = fileList.filter(f => f.raw && f.status !== 'removed'); // 新选择图片

      // 为新选择的图片添加预览URL
      const newImagesWithPreview = newImages.map((file, index) => ({
        name: file.name,
        url: URL.createObjectURL(file.raw), // 生成预览URL
        raw: file.raw, // 保留原始文件对象用于上传
        status: 'success'
      }));

      // 合并现有图片和新图片
      this.imageFileList = [...existingImages, ...newImagesWithPreview];
    },

    // 预览图片移除
    onImageRemove(file, fileList) {
      // 如果删除的是现有图片（有url属性），记录到删除列表
      if (file.url && !file.raw) {
        // 检查是否已经在删除列表中，避免重复添加
        if (!this.deletePreviewUrls.includes(file.url)) {
          this.deletePreviewUrls.push(file.url);
        }
      }

      // 更新新选择的文件列表 - 移除已被删除的文件
      this.newImageFiles = fileList
        .filter(f => f.raw && f.status !== 'removed')
        .map(f => f.raw);

      // 更新显示列表 - 同步移除删除的图片
      this.imageFileList = fileList.filter(f => f.status !== 'removed');
    },

     // 提交修改
     async handleSubmit() {
       try {
        // 根据文件编码判断是否为新文科文件，新文科文件需要验证分类选择
        const isXinWenKeFile = this.isXinWenKeFile(this.fileData);
        if (isXinWenKeFile) {
           if (!this.form.categoryId) {
             this.$message.error('请选择大分类');
             return;
           }
           if (!this.form.subcategoryId) {
             this.$message.error('请选择子分类');
             return;
           }
         }
         
         await this.$refs.editForm.validate();
         
         this.submitting = true;
        
        // 构建FormData，支持文件上传
        const formData = new FormData();
        
        // 基础信息
        formData.append('code', this.fileData.code); // 使用文件编码定位文件
        formData.append('name', this.form.name);
        formData.append('author', this.form.author);
        formData.append('pageCount', this.form.page);
        formData.append('price', this.form.price);
        formData.append('state', this.form.state);
        formData.append('type', this.form.type);
        formData.append('format', this.form.format || '');
        formData.append('describes', this.form.description || '');
        formData.append('remark', this.form.remark || ''); // 添加备注字段
        
        // 模块特有字段处理
        if (this.module === 'xinwenke') {
          // 新文科模块：使用categoryId和subcategoryId
          if (this.form.categoryId) {
            formData.append('categoryId', this.form.categoryId);
          }
          if (this.form.subcategoryId) {
            formData.append('subcategoryId', this.form.subcategoryId);
          }
        } else {
          // 其他模块：根据需要添加tag1Id等标签参数
          // 这里可以根据具体需求添加标签参数，目前保持原有逻辑
        }
        
        // 增量更新模式
        formData.append('incrementalUpdate', this.incrementalUpdate.toString());

        // 封面图片更新（可选）
        if (this.newCoverFile) {
          formData.append('coverFile', this.newCoverFile);
        } else if (this.deleteCoverUrl) {
          // 删除封面图
          formData.append('deleteCoverUrl', this.deleteCoverUrl);
        }

        // 预览图片更新（可选）
        if (this.newImageFiles && this.newImageFiles.length > 0) {
          this.newImageFiles.forEach((file) => {
            formData.append('previewFiles', file);
          });
        }

        // 删除的预览图片
        if (this.deletePreviewUrls && this.deletePreviewUrls.length > 0) {
          this.deletePreviewUrls.forEach((imageUrl) => {
            formData.append('deletePreviewUrls', imageUrl);
          });
        }
        
        // 根据文件编码判断是否为新文科文件，选择不同的更新接口
        let response;
        if (isXinWenKeFile) {
          // 新文科模块使用独立的接口（multipart/form-data格式，支持文件上传）
          // 为新文科模块构建FormData
          const xinwenkeFormData = new FormData();
          xinwenkeFormData.append('code', this.fileData.code);
          xinwenkeFormData.append('name', this.form.name);
          xinwenkeFormData.append('author', this.form.author);
          xinwenkeFormData.append('pageCount', this.form.page);
          xinwenkeFormData.append('price', this.form.price);
          xinwenkeFormData.append('state', this.form.state);
          xinwenkeFormData.append('format', this.form.format || '');
          xinwenkeFormData.append('describes', this.form.description || '');
          xinwenkeFormData.append('remark', this.form.remark || '');

          // 新文科特有字段
          if (this.form.categoryId) {
            xinwenkeFormData.append('categoryId', this.form.categoryId);
          }
          if (this.form.subcategoryId) {
            xinwenkeFormData.append('subcategoryId', this.form.subcategoryId);
          }

          // 增量更新模式
          xinwenkeFormData.append('incrementalUpdate', this.incrementalUpdate.toString());

          // 封面图片更新（可选）
          if (this.newCoverFile) {
            xinwenkeFormData.append('coverFile', this.newCoverFile);
          } else if (this.deleteCoverUrl) {
            // 删除封面图
            xinwenkeFormData.append('deleteCoverUrl', this.deleteCoverUrl);
          }

          // 预览图片更新（可选）
          if (this.newImageFiles && this.newImageFiles.length > 0) {
            this.newImageFiles.forEach((file) => {
              xinwenkeFormData.append('previewFiles', file);
            });
          }

          // 删除的预览图片
          if (this.deletePreviewUrls && this.deletePreviewUrls.length > 0) {
            this.deletePreviewUrls.forEach((imageUrl) => {
              xinwenkeFormData.append('deletePreviewUrls', imageUrl);
            });
          }


          response = await api.updateXinWenKeFile(xinwenkeFormData);
        } else {
          // 其他模块使用通用更新接口（支持文件上传）

          response = await api.updateUniversalFile(formData);
        }
        
        if (response.data && response.data.code === 200) {
          this.$message.success('文件修改成功！');
          this.$emit('edit-success');
          this.handleCancel();
        } else {
          this.$message.error(response.data?.msg || '文件修改失败！');
        }
      } catch (error) {
        this.$message.error('提交失败！');
      } finally {
        this.submitting = false;
      }
    },

    // 取消编辑
    handleCancel() {
      this.$emit('edit-cancel');
    },

    // 强制更新上传组件
    forceUpdateUploadComponents() {
      // 使用更简单的方法强制更新组件
      this.$forceUpdate();
    }
  }
};
</script>

<style scoped>
.file-edit-container {
  padding: 15px;
  background: #fff;
  border-radius: 8px;
}

.edit-form {
  max-width: 100%;
}

.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.form-item-half {
  flex: 1;
}

.form-item-full {
  width: 100%;
}

.file-info-section {
  margin: 30px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.file-info-section h4 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.file-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.file-info-item {
  display: flex;
  align-items: center;
}

.file-info-item .label {
  font-weight: 500;
  color: #666;
  margin-right: 8px;
  min-width: 60px;
}

.file-info-item .value {
  color: #333;
  flex: 1;
}

.file-media-section {
  margin: 30px 0;
  padding: 20px;
  background: #f0f9ff;
  border-radius: 8px;
  border: 1px solid #b3e5fc;
}

.file-media-section h4 {
  margin: 0 0 10px 0;
  color: #1890ff;
  font-size: 16px;
  font-weight: 600;
}

.media-tip {
  margin: 0 0 20px 0;
  color: #1890ff;
  font-size: 14px;
  line-height: 1.5;
}

.file-upload-item {
  margin-bottom: 20px;
}

.upload-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #999;
}

.media-info {
  margin-top: 15px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .form-item-half {
    margin-bottom: 20px;
  }
  
  .file-info-grid {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .form-actions .el-button {
    width: 100%;
  }
}
</style>
